import { usersId, type UsersId } from "./Users";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for sessions */
export type SessionsId = string & { __flavor: "SessionsId" };

/**
 * Represents the table public.sessions
 * @uniqueConstraints {"sessions_pkey":{"name":"sessions_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"sessions_token_unique":{"name":"sessions_token_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"token","definition":"token","predicate":null}]},"sessions_user_id_index":{"name":"sessions_user_id_index","isPrimary":false,"isUnique":false,"columns":[{"name":"user_id","definition":"user_id","predicate":null}]},"sessions_token_index":{"name":"sessions_token_index","isPrimary":false,"isUnique":false,"columns":[{"name":"token","definition":"token","predicate":null}]}}
 */
export default interface SessionsTable {
  id: ColumnType<SessionsId, SessionsId, SessionsId>;

  expiresAt: ColumnType<Date, Date | string, Date | string>;

  token: ColumnType<string, string, string>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string, Date | string>;

  ipAddress: ColumnType<string | null, string | null, string | null>;

  userAgent: ColumnType<string | null, string | null, string | null>;

  userId: ColumnType<UsersId, UsersId, UsersId>;

  activeOrganizationId: ColumnType<string | null, string | null, string | null>;

  impersonatedBy: ColumnType<string | null, string | null, string | null>;
}

export type Sessions = Selectable<SessionsTable>;

export type NewSessions = Insertable<SessionsTable>;

export type SessionsUpdate = Updateable<SessionsTable>;

export const sessionsId = z.string() as unknown as z.Schema<SessionsId>;

export const sessions = z.object({
  id: sessionsId,
  expiresAt: z.date(),
  token: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  userId: usersId,
  activeOrganizationId: z.string().nullable(),
  impersonatedBy: z.string().nullable(),
});

export const sessionsInitializer = z.object({
  id: sessionsId,
  expiresAt: z.date(),
  token: z.string(),
  createdAt: z.date().optional(),
  updatedAt: z.date(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  userId: usersId,
  activeOrganizationId: z.string().optional().nullable(),
  impersonatedBy: z.string().optional().nullable(),
});

export const sessionsMutator = z.object({
  id: sessionsId.optional(),
  expiresAt: z.date().optional(),
  token: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  userId: usersId.optional(),
  activeOrganizationId: z.string().optional().nullable(),
  impersonatedBy: z.string().optional().nullable(),
});

// ── constraints for Sessions ─────────────────────────────────
export const SessionsConstraints = {
  sessions_pkey: {
    name: "sessions_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  sessions_token_unique: {
    name: "sessions_token_unique",
    isPrimary: false,
    isUnique: true,
    columns: [{ name: "token", definition: "token", predicate: null }],
  },
  sessions_user_id_index: {
    name: "sessions_user_id_index",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "user_id", definition: "user_id", predicate: null }],
  },
  sessions_token_index: {
    name: "sessions_token_index",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "token", definition: "token", predicate: null }],
  },
} as const;
export type SessionsConstraintsType = typeof SessionsConstraints;
